import React from 'react';
import {StyleSheet, View} from 'react-native';
import {useTranslation} from 'react-i18next';

import {usePwa} from '@/shared/lib/pwa';
import {AppButton} from '@/shared/ui/button';
import {Card, IconBubble} from '@/shared/ui/card';
import {Icon} from '@/shared/ui/icons';
import {AppText} from '@/shared/ui/text';
import {spacing, useTheme} from '@/shared/ui/theme';

/**
 * "Установить приложение" — the card on the profile screen that turns the browser tab
 * into something on the home screen.
 *
 * Three states, and which one shows is decided entirely by the browser:
 *
 *   already installed  → nothing renders. Asking someone to install the app they are
 *                        currently running inside is the classic PWA embarrassment.
 *   Chromium           → a button. The `beforeinstallprompt` event was captured at
 *                        startup (shared/lib/pwa.web.ts) and replaying it opens the
 *                        real system dialog.
 *   iOS Safari         → instructions, no button. Safari supports installing but
 *                        exposes no API for it, so the only honest UI is telling the
 *                        user which two menu items to tap.
 *
 * On native this is `null` — `usePwa()` reports `isStandalone: true` there, which an
 * APK already is.
 */
const styles = StyleSheet.create({
  root: {gap: spacing(3)},
  head: {flexDirection: 'row', alignItems: 'center', gap: spacing(3)},
  text: {flex: 1, gap: 2},
  steps: {gap: spacing(1)},
});

export function InstallCard() {
  const {t} = useTranslation();
  const p = useTheme();
  const {canInstall, promptInstall, isStandalone, needsManualInstall} = usePwa();

  if (isStandalone || (!canInstall && !needsManualInstall)) {
    return null;
  }

  return (
    <Card padding={4} style={styles.root} testID="pwa-install">
      <View style={styles.head}>
        <IconBubble size={40} tone={p.accent} square>
          <Icon name="home" size={20} color={p.primary} />
        </IconBubble>
        <View style={styles.text}>
          <AppText variant="heading">{t('pwa.installTitle')}</AppText>
          <AppText variant="caption">{t('pwa.installHint')}</AppText>
        </View>
      </View>

      {canInstall ? (
        <AppButton
          title={t('pwa.installAction')}
          icon="plus"
          tone="soft"
          onPress={() => {
            void promptInstall();
          }}
          testID="pwa-install-button"
        />
      ) : (
        <View style={styles.steps}>
          <AppText variant="caption">{t('pwa.installIosStep1')}</AppText>
          <AppText variant="caption">{t('pwa.installIosStep2')}</AppText>
        </View>
      )}
    </Card>
  );
}
